import { useEffect } from "react";
import { Field, useFormState } from "react-final-form";
import { useIntl } from "react-intl";
import Section from "../../../common/section";
import * as Validations from "../../../common/form/validator";
import {
  DropdownInput,
  TextInput,
  MultiSelectInput,
} from "../../../common/fields";
import {
  LearningObjectConstants,
  GlobalConstants,
} from "../../../../constants";
import { withSelected } from "../../../common/hooks/useServerSearch";

const LearningObjectDetailsForm = ({
  disabled = false,
  setUploadType,
  uploadDetails = null,
  readOnly = false,
  allowedLanguages = ["en"],
  certificates,
  rewards = [],
  // Searches the server as the admin types in the reward picker
  onRewardSearch,
  // The object's current reward ({ id, name }), shown even when a search left it out
  selectedReward,
}) => {
  const intl = useIntl();
  const formState = useFormState();

  useEffect(() => {
    if (formState?.values?.type) {
      setUploadType(formState?.values?.type);
    }
  }, [formState?.values?.type, setUploadType]);

  const getAllSupportedLanguages = () => {
    return GlobalConstants.LANGUAGES.filter(
      (lang) => allowedLanguages.includes(lang.code) || lang.code === "en"
    ).map((language) => ({
      ...language,
      id: language.code,
      name: language.name,
    }));
  };

  return (
    <div>
      <Section title="identity">
        <div className="grid">
          <div className="col-12 md:col-6 lg:col-4">
            <Field
              name="name"
              component={TextInput}
              type="text"
              label={intl.formatMessage({
                id: "name",
              })}
              disabled={disabled}
              placeholder={intl.formatMessage({
                id: "name",
              })}
              required
              validate={Validations.composeValidators(Validations.required)}
            />
          </div>
          <div className="col-12 md:col-6 lg:col-4">
            <Field
              name="type"
              component={DropdownInput}
              options={LearningObjectConstants.TYPES}
              label={intl.formatMessage({
                id: "type",
              })}
              placeholder={intl.formatMessage({
                id: "type",
              })}
              required
              disabled={disabled}
              // Using constant options; disable client filtering for small static list
              filter={false}
              readOnly={uploadDetails ? true : false || readOnly}
              validate={Validations.composeValidators(Validations.required)}
            />
          </div>

          {/* Google Form URL field - Only shown for type 11 (Google Form) */}
          {(formState.values.type === 11 || formState.values.type === "11") && (
            <div className="col-12 md:col-6 lg:col-4">
              <Field
                name="content"
                component={TextInput}
                type="url"
                label={intl.formatMessage({
                  id: "google_form_url",
                })}
                disabled={disabled}
                readOnly={readOnly}
                placeholder={intl.formatMessage({
                  id: "enter_google_form_url",
                })}
                required
                validate={Validations.composeValidators(
                  Validations.required,
                  Validations.validateGoogleFormURL
                )}
              />
            </div>
          )}

          {/* <div className="col-12 md:col-6 lg:col-4">
            <Field
              name="maxAttemptsAllowed"
              component={TextInput}
              type="number"
              label={intl.formatMessage({
                id: "max_attempts_allowed",
              })}
              disabled={disabled}
              placeholder={intl.formatMessage({
                id: "max_attempts_allowed",
              })}
              required
              validate={Validations.composeValidators(
                Validations.required,
                Validations.numberGreater0
              )}
            />
          </div> */}
          <div className="col-12 md:col-6 lg:col-4">
            <Field
              name="maxTimeInSecsAllowed"
              component={TextInput}
              type="number"
              label={intl.formatMessage({
                id: "max_time_in_seconds",
              })}
              disabled={disabled}
              placeholder={intl.formatMessage({
                id: "max_time_in_seconds",
              })}
              required
              validate={Validations.composeValidators(
                Validations.required,
                Validations.numberGreater0
              )}
            />
          </div>
          <div className="col-12 md:col-6 lg:col-4">
            <Field
              name="certificateId"
              component={DropdownInput}
              label={intl.formatMessage({
                id: "certficate",
              })}
              placeholder={intl.formatMessage({
                id: "certficate",
              })}
              disabled={disabled}
              filter={true}
              options={certificates.map((item) => ({
                name: item.name,
                id: item.id,
                value: item.id,
                i18n: false,
              }))}
            />
          </div>
          {/* Reward Assignment - Simple fields without triggers */}
          <div className="col-12 md:col-6 lg:col-4">
            <Field
              name="rewardId"
              component={DropdownInput}
              label={intl.formatMessage({
                id: "reward",
              })}
              placeholder={intl.formatMessage({
                id: "reward",
              })}
              disabled={disabled}
              filter={true}
              options={withSelected(
                (rewards || []).map((item) => ({
                  name: item.name,
                  id: item.id,
                  value: item.id,
                  i18n: false,
                })),
                selectedReward && {
                  ...selectedReward,
                  value: selectedReward.id,
                  i18n: false,
                }
              )}
              onFilter={onRewardSearch}
            />
          </div>
          <div className="col-12 md:col-6 lg:col-4">
            <Field
              name="pointsOnCompletion"
              component={TextInput}
              type="number"
              label={intl.formatMessage({
                id: "point_on_completion",
              })}
              disabled={disabled}
              placeholder={intl.formatMessage({
                id: "point_on_completion",
              })}
              validate={Validations.numberPositive}
            />
          </div>
          {/* <div className="col-12 md:col-6 lg:col-4">
            <Field
              name="tags"
              component={Tags}
              type="text"
              label={intl.formatMessage({
                id: "tags",
              })}
              disabled={disabled}
              placeholder={intl.formatMessage({
                id: "tags",
              })}
            />
          </div> */}
          <div className="col-12 md:col-6 lg:col-4">
            <Field
              name="supportedLanguages"
              component={MultiSelectInput}
              options={getAllSupportedLanguages()}
              label={intl.formatMessage({
                id: "supported_languages",
              })}
              disabled={disabled}
              placeholder={intl.formatMessage({
                id: "supported_languages",
              })}
            />
          </div>
        </div>
      </Section>
    </div>
  );
};

export default LearningObjectDetailsForm;
